import { APP_SCOPE, QUERY_TYPE } from '@nx/constants';
import type { ParseableError } from '@nx/errors';
import { isNeo4jError, isParseableError } from '@nx/errors';
import { MINIMUM_SUPPORTED_VERSION, cypherDataToString, isCypherBasicPropertyType } from '@nx/neo4j-sdk';
import type { CypherBasicPropertyType } from '@nx/neo4j-sdk/src/types/cypher-data-types';
import type { Database, RunQueryPayload } from '@nx/state';
import {
  DriverConnectionError,
  NoDatabaseSelectedError,
  OperationNotSupportedOnSystemDatabaseError,
  StoreKit,
  isDatabaseV4,
  isDatabaseV5,
  runQuery,
  selectDatabases,
  selectMetadata,
} from '@nx/state';
import { isNullish } from '@nx/stdlib';
import { SysInfoCmdErrors, UnknownErrors } from '@query/constants/errors';
import { registerAbortFunction, unregisterAbortFunction } from '@query/services/abortable-frame-service';
import {
  NON_TRACKABLE_COMMAND_SOURCES,
  trackFailedSysInfoCmd,
  trackSuccessfulSysInfoCmd,
} from '@query/services/analytics';
import { cacheCategoryMetrics } from '@query/services/frame-sysinfo-service';
import type { SysInfoMappedMetrics, SysInfoMetric, SysInfoMetricCategory } from '@query/utils/sysinfo-utils';
import {
  DATABASE_METRIC_TYPE,
  DBMS_METRIC_TYPE,
  createClientConfig,
  formatBytes,
  formatMetricJmxQuery,
  formatPercentage,
  isUnsupportedVersion,
} from '@query/utils/sysinfo-utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { Record as Neo4jRecord } from 'neo4j-driver';

import type { ExecuteCommandPayload } from './command-thunks';
import type { SysInfoRequestId } from './requests-slice';

const RESULT_LIMIT = 1000;

const metricCategories: SysInfoMetricCategory[] = [
  {
    key: 'id-allocation',
    category: 'ID Allocation',
    metrics: {
      'Node ID': {
        type: DATABASE_METRIC_TYPE,
        name: 'neo4j.count.node',
        fallback: 'ids_in_use.node',
      },
      'Relationship ID': {
        type: DATABASE_METRIC_TYPE,
        name: 'neo4j.count.relationship',
        fallback: 'ids_in_use.relationship',
      },
      'Relationship Type ID': {
        type: DATABASE_METRIC_TYPE,
        name: 'neo4j.count.relationship_types',
        fallback: 'ids_in_use.relationship_type',
      },
    },
  },
  {
    key: 'store-size',
    category: 'Store Size',
    metrics: {
      Total: {
        type: DATABASE_METRIC_TYPE,
        name: 'store.size.full',
        fallback: 'store.size.total',
        mapFn: (value) => formatBytes(value),
      },
      Database: {
        type: DATABASE_METRIC_TYPE,
        name: 'store.size.database',
        mapFn: (value) => formatBytes(value),
      },
    },
  },
  {
    key: 'page-cache',
    category: 'Page Cache',
    metrics: {
      Hits: {
        type: DBMS_METRIC_TYPE,
        name: 'page_cache.hits',
      },
      'Hit Ratio': {
        type: DBMS_METRIC_TYPE,
        name: 'page_cache.hit_ratio',
        mapFn: (value) => formatPercentage(value),
      },
      'Usage Ratio': {
        type: DBMS_METRIC_TYPE,
        name: 'page_cache.usage_ratio',
        mapFn: (value) => formatPercentage(value),
      },
      'Page Faults': {
        type: DBMS_METRIC_TYPE,
        name: 'page_cache.page_faults',
      },
    },
  },
  {
    key: 'transactions',
    category: 'Transactions',
    metrics: {
      'Last Tx Id': {
        type: DATABASE_METRIC_TYPE,
        name: 'transaction.last_committed_tx_id',
      },
      'Current Read': {
        type: DATABASE_METRIC_TYPE,
        name: 'transaction.active_read',
      },
      'Current Write': {
        type: DATABASE_METRIC_TYPE,
        name: 'transaction.active_write',
      },
      'Peak Transactions': {
        type: DATABASE_METRIC_TYPE,
        name: 'transaction.peak_concurrent',
      },
      'Committed Read': {
        type: DATABASE_METRIC_TYPE,
        name: 'transaction.committed_read',
      },
      'Committed Write': {
        type: DATABASE_METRIC_TYPE,
        name: 'transaction.committed_write',
      },
    },
  },
];

const isSystemOrCompositeDb = (database: Database) => {
  if (isDatabaseV5(database)) {
    const dbType = database.type;
    return dbType === 'system' || dbType === 'composite' || database.name === 'system';
  } else if (isDatabaseV4(database)) {
    return database.name === 'system';
  }

  return false;
};

const extractKeyValuePairFromRecord = (
  record: Neo4jRecord,
): { key: string; value: CypherBasicPropertyType | undefined } => {
  const name: unknown = record.get('Name');
  const key = isCypherBasicPropertyType(name) ? cypherDataToString(name, 'json', false) : '';
  const value: unknown = record.get('Value');
  return { key, value: isCypherBasicPropertyType(value) ? value : undefined };
};

const queryAndCacheMetrics = async (frameId: string, requestId: SysInfoRequestId, database: string): Promise<void> => {
  const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
  const { configuration } = selectMetadata(globalStore.getState());
  const clientConfig = createClientConfig(configuration?.serverMetricsPrefix, configuration?.namespacesEnabled);

  const processMetricCategory = async (category: SysInfoMetricCategory) => {
    const result: SysInfoMappedMetrics = Object.keys(category.metrics).reduce<SysInfoMappedMetrics>((metrics, name) => {
      metrics[name] = { name, value: undefined };
      return metrics;
    }, {});

    const query = `
        UNWIND $metrics as metric
        CALL dbms.queryJmx(metric.name) YIELD name, attributes
        WITH metric.displayName AS Name, attributes.Value.value AS Value, attributes.Count.value AS Count
        RETURN Name, CASE WHEN Value IS NOT NULL THEN Value ELSE Count END AS Value;`;

    const config = {
      recordLimit: RESULT_LIMIT,
      sessionConfig: { database },
      query,
      metadata: { appScope: APP_SCOPE.query, queryType: QUERY_TYPE.UserAction },
      transactionMode: 'read',
    } satisfies RunQueryPayload;

    const primaryMetrics = Object.entries(category.metrics).map(([key, metric]) => ({
      displayName: key,
      name: formatMetricJmxQuery(clientConfig, metric.type, metric.name, database),
    }));
    const primaryMetricsQuery = globalStore.dispatch(
      runQuery({
        parameters: {
          metrics: primaryMetrics,
        },
        ...config,
      }),
    );

    const fallbackMetrics = Object.entries(category.metrics)
      .filter(([, metric]) => metric.fallback !== undefined && metric.fallback !== '')
      .map(([key, metric]) => ({
        displayName: key,
        name: formatMetricJmxQuery(clientConfig, metric.type, metric.fallback, database),
      }));
    const fallbackMetricsQuery = globalStore.dispatch(
      runQuery({
        parameters: {
          metrics: fallbackMetrics,
        },
        ...config,
      }),
    );

    const metrics = Promise.all([primaryMetricsQuery.unwrap(), fallbackMetricsQuery.unwrap()]);

    const abortMetricQueries = () => {
      primaryMetricsQuery.abort();
      fallbackMetricsQuery.abort();
    };
    registerAbortFunction(requestId, abortMetricQueries);

    const [rawPrimaryMetrics, rawFallbackMetrics] = await metrics;
    const records = rawFallbackMetrics.records.concat(rawPrimaryMetrics.records);
    records.forEach((record) => {
      const { key, value } = extractKeyValuePairFromRecord(record);
      if (result[key] !== undefined && value !== undefined) {
        const metric: SysInfoMetric | undefined = category.metrics[key];
        const mapFn = metric?.mapFn ?? cypherDataToString;
        result[key].value = mapFn(value);
      }
    });

    cacheCategoryMetrics(frameId, category.key, result);
  };

  await Promise.all(metricCategories.map(processMetricCategory));
};

export type ExecutedSysInfoCommandPayload = {
  frameId: string;
  requestId: SysInfoRequestId;
  command: ExecuteCommandPayload;
};

export type ExecutedSysInfoCommand = { requestId: SysInfoRequestId };

export const executeSysInfoCommandThunk = createAsyncThunk<
  ExecutedSysInfoCommand,
  ExecutedSysInfoCommandPayload,
  {
    rejectValue: { error: Partial<ParseableError>; aborted: boolean };
  }
>('stream/executeSysInfoCommand', async (payload: ExecutedSysInfoCommandPayload, { rejectWithValue }) => {
  const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
  const { requestId, command } = payload;
  const { connection, source } = command;

  if (isNullish(connection)) {
    const error = new DriverConnectionError();
    return rejectWithValue({
      error,
      aborted: false,
    });
  }

  const { database, version } = connection;
  const databases = selectDatabases(globalStore.getState());
  const selectedDatabase = databases.find((db) => db.name === database);

  try {
    if (isNullish(selectedDatabase)) {
      const error = new NoDatabaseSelectedError();
      return rejectWithValue({
        error,
        aborted: false,
      });
    }

    if (isSystemOrCompositeDb(selectedDatabase)) {
      const error = new OperationNotSupportedOnSystemDatabaseError(
        'The :sysinfo command is not supported while using the system or a composite database.',
      );
      return rejectWithValue({
        error,
        aborted: false,
      });
    }

    if (isUnsupportedVersion(version)) {
      return rejectWithValue({
        error: SysInfoCmdErrors.UnsupportedVersion(MINIMUM_SUPPORTED_VERSION),
        aborted: false,
      });
    }

    await queryAndCacheMetrics(payload.frameId, requestId, selectedDatabase.name);

    if (!NON_TRACKABLE_COMMAND_SOURCES.includes(source)) {
      trackSuccessfulSysInfoCmd();
    }
  } catch (error) {
    if (isParseableError(error)) {
      const errorCode = isNeo4jError(error) ? error.code : error.name;
      trackFailedSysInfoCmd(errorCode);
      return rejectWithValue({ error, aborted: error.name === 'AbortError' });
    }

    trackFailedSysInfoCmd();
    return rejectWithValue({ error: UnknownErrors.UnknownError, aborted: false });
  } finally {
    unregisterAbortFunction(requestId);
  }

  return { requestId: payload.requestId };
});
